// components/Forms/AddTicketForm.js

import { useState, useEffect } from "react";
import styled from "styled-components";
import InputText from "../Input/InputText";
import Button from "../Button/Button";
import Switch from "../Switch";
import Typography from "../Typography";
import ToogableTabs from "../Toogle/ToogableTabs"; // Importa ToogableTabs
import { theme } from "../../theme";
import { toast } from "react-toastify"; // Importa toast para notificaciones
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import Tooltip from "../Tooltip/Tooltip";
import InfoCircleOutlineIcon from "../Icons/InfoCircleOutlineIcon";
import ArrowRightIcon from "../Icons/ArrowRightIcon";
import { TICKET_FREE, TICKET_PAID } from "../../constants/eventTypes";
import { openUserSettingsModal } from "../../utils/settingsNavigation";

const AddTicketForm = ({ onSubmit }) => {
	const { t } = useTranslation("ticketForms");
	const { user, stripeStatus, refreshStripeStatus } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	// Opciones para ToogableTabs
	const ticketOptions = [
		{ label: t("free"), value: TICKET_FREE },
		{ label: t("paid"), value: TICKET_PAID },
	];
	const [formData, setFormData] = useState({
		name: "",
		type: TICKET_FREE,
		price: 0,
		approvalRequired: false,
		capacity: 10,
	});
	const [errors, setErrors] = useState({});

	// Al montar, refrescamos el estado de Stripe
	useEffect(() => {
		if (user) {
			const userId = user._id || user.id;
			refreshStripeStatus(userId);
		}
	}, [user, refreshStripeStatus]);

	// Manejar cambios en las pestañas de tipo de ticket
	const handleTabChange = (value) => {
		setFormData((prev) => ({
			...prev,
			type: value,
			// Si se cambia a 'paid' y el precio es 0, establecerlo en 10
			price: value === TICKET_PAID && prev.price === 0 ? 10 : prev.price,
			// Si se cambia a 'free', desactivar la aprobación requerida
			approvalRequired: value === TICKET_FREE ? prev.approvalRequired : false,
		}));
	};

	// Validación del formulario
	const validate = () => {
		const newErrors = {};
		if (!formData.name.trim()) newErrors.name = t("nameRequired");
		if (
			formData.type === TICKET_PAID &&
			(formData.price === undefined || formData.price <= 0)
		) {
			newErrors.price = t("priceRequired");
		}
		if (!formData.capacity || formData.capacity < 1) {
			newErrors.capacity = t("capacityRequired");
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Manejar el envío del formulario
	const handleSubmit = (e) => {
		e.preventDefault();
		if (!validate()) {
			toast.error("Por favor, corrige los errores en el formulario.");
			return;
		}
		onSubmit(formData);
	};

	return (
		<Form onSubmit={handleSubmit}>
			<FormFields>
				{/* Nombre del Ticket */}
				<InputWrapper>
					<Typography
						$variant="body-2-semibold"
						color={theme.colors.defaulStrong}
						as="label"
					>
						Nombre
					</Typography>
					<InputText
						type="text"
						name="name"
						$size=""
						value={formData.name}
						onChange={(e) =>
							setFormData((prev) => ({ ...prev, name: e.target.value }))
						}
						placeholder={t("namePlaceholder")}
						required
					/>
					{errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
				</InputWrapper>

				{/* Tipo de Ticket */}
				<InputWrapper>
					{/* <Typography $variant="body-2-medium" as="label">Tipo</Typography> */}
					<ToogableTabs
						options={ticketOptions}
						activeOption={formData.type}
						onTabChange={handleTabChange}
					/>
				</InputWrapper>

				{formData.type === TICKET_PAID ? (
					<>
						{stripeStatus.loading ? (
							<p>{t("loadingStripe")}</p>
						) : (
							<>
								{!stripeStatus.hasStripeAccount ? (
									<ConnectedAccountBanner>
										<IntegrationIcon
											src="/icons/motorik-stripe-connect.svg"
											alt="Stripe Integration"
										/>
										<Typography as="h3" $variant="body-1-semibold">
											{t("setupAccountTitle")}
										</Typography>
										<Typography
											as="p"
											$variant="body-2-medium"
											color={theme.colors.defaultWeak}
										>
											{t("setupAccountDescription")}
										</Typography>
										<StripeActions>
											<Button
												size="l"
												sizeTablet="l"
												sizeMobile="m"
												onClick={() =>
													openUserSettingsModal({
														navigate,
														location,
														userId: user?._id || user?.id,
													})
												}
											>
												{t("goToSettings")}
												<ArrowRightIcon
													width="20px"
													height="20px"
													fill={theme.colors.inverseMain}
												/>
											</Button>
										</StripeActions>
									</ConnectedAccountBanner>
								) : !stripeStatus.chargesEnabled ? (
									<ConnectedAccountBanner>
										<IntegrationIcon
											src="/icons/motorik-stripe-connect.svg"
											alt="Stripe Integration"
										/>
										<Typography as="h3" $variant="body-1-semibold">
											{t("chargesDisabledTitle")}
										</Typography>
										<Typography as="p" $variant="body-2-regular">
											{t("chargesDisabledDescription")}
										</Typography>
										<Button
											size="l"
											sizeTablet="l"
											sizeMobile="m"
											onClick={() =>
												openUserSettingsModal({
													navigate,
													location,
													userId: user?._id || user?.id,
												})
											}
										>
											{t("goToSettings")}
											<ArrowRightIcon
												width="20px"
												height="20px"
												fill={theme.colors.inverseMain}
											/>
										</Button>
									</ConnectedAccountBanner>
								) : (
									<>
										<InputWrapper>
											<Typography $variant="body-2-semibold" as="label">
												{t("price")}
											</Typography>
											<InputText
												type="number"
												name="price"
												value={formData.price}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														price: Number(e.target.value),
													}))
												}
												placeholder={t("pricePlaceholder")}
												required
											/>
											{errors.price && (
												<ErrorMessage>{errors.price}</ErrorMessage>
											)}
										</InputWrapper>
										<InputWrapper>
											{/* <Typography $variant="body-2-semibold" as="label">Capacidad</Typography> */}
											<InputLabelWrapper>
												<Typography
													$variant="body-2-semibold"
													color={theme.colors.defaulStrong}
													as="label"
												>
													{t("capacity")}
												</Typography>
												<Tooltip
													text={t("capacityTooltip")}
													maxWidth="220px"
													position="right"
													style={{ marginLeft: "-4px" }}
												>
													<InfoButton>
														<InfoCircleOutlineIcon
															width="20px"
															height="20px"
															fill={theme.colors.defaultSubtle}
														/>
													</InfoButton>
												</Tooltip>
											</InputLabelWrapper>
											<InputText
												type="number"
												name="capacity"
												value={formData.capacity}
												onChange={(e) =>
													setFormData((prev) => ({
														...prev,
														capacity: Number(e.target.value),
													}))
												}
												placeholder={t("capacityPlaceholder")}
												required
											/>
											{errors.capacity && (
												<ErrorMessage>{errors.capacity}</ErrorMessage>
											)}
										</InputWrapper>
									</>
								)}
							</>
						)}
					</>
				) : (
					<>
						<InputWrapper>
							{/* <Typography $variant="body-2-medium" as="label">Capacidad:</Typography> */}
							<InputLabelWrapper>
								<Typography
									$variant="body-2-semibold"
									color={theme.colors.defaulStrong}
									as="label"
								>
									{t("capacity")}
								</Typography>
								<Tooltip
									text={t("capacityTooltip")}
									maxWidth="220px"
									position="right"
									style={{ marginLeft: "-4px" }}
								>
									<InfoButton>
										<InfoCircleOutlineIcon
											width="20px"
											height="20px"
											fill={theme.colors.defaultSubtle}
										/>
									</InfoButton>
								</Tooltip>
							</InputLabelWrapper>
							<InputText
								type="number"
								name="capacity"
								value={formData.capacity}
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										capacity: Number(e.target.value),
									}))
								}
								placeholder={t("capacityPlaceholder")}
								required
							/>
							{errors.capacity && (
								<ErrorMessage>{errors.capacity}</ErrorMessage>
							)}
						</InputWrapper>
						<InputWrapperInline>
							<InputLabelWrapper>
								<Typography
									$variant="body-2-semibold"
									color={theme.colors.defaulStrong}
									as="label"
								>
									{t("approvalRequired")}
								</Typography>
								<Tooltip
									text={t("approvalRequiredTooltip")}
									maxWidth="240px"
									position="right"
									style={{ marginLeft: "-4px" }}
								>
									<InfoButton>
										<InfoCircleOutlineIcon
											width="20px"
											height="20px"
											fill={theme.colors.defaultSubtle}
										/>
									</InfoButton>
								</Tooltip>
							</InputLabelWrapper>
							{/* <Typography $variant="body-2-medium" as="label">Aprobación Requerida:</Typography> */}
							<Switch
								value={formData.approvalRequired}
								ariaLabel={t("approvalRequired")}
								onChange={(value) =>
									setFormData((prev) => ({ ...prev, approvalRequired: value }))
								}
							/>
						</InputWrapperInline>
					</>
				)}
			</FormFields>
			<FormActions>
				<Button
					style={{ justifyContent: "center" }}
					type="submit"
					size="l"
					sizeTablet="l"
					sizeMobile="m"
					disabled={
						formData.type === TICKET_PAID &&
						(!stripeStatus.hasStripeAccount || !stripeStatus.chargesEnabled)
					}
				>
					{t("createTicket")}
				</Button>
			</FormActions>
		</Form>
	);
};

export default AddTicketForm;

// Estilos para el formulario
const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: 16px;
  width: 100%;
`;

const FormActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  text-align: center;
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputWrapperInline = styled(InputWrapper)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const InputLabelWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs}; 
  width: 100%;
`;

const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
`;

const ConnectedAccountBanner = styled.div`
  align-items: flex-start;
  background: ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.sm};
`;
const IntegrationIcon = styled.img`
  max-height: 40px;
  padding-bottom: ${({ theme }) => theme.sizing.xs};
  width: auto;
`;
const StripeActions = styled.div`
  padding: ${({ theme }) => theme.sizing.sm} 0 0 0;
`;

const InfoButton = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.sizing.md};
  justify-content: center;
  transition: background-color 0.3s ease-in-out;
  width: ${({ theme }) => theme.sizing.md};

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;
